import { createContextApi, invalidatesOne, providesList, providesOne, createCursorPagedEndpoint, registerCrossContextInvalidation } from '@quickbee/store';

// src/index.ts
var LEDGER_TAGS = [
  "LedgerEntry",
  "LedgerAccount",
  "Statement",
  "SuspenseCase",
  "VaultReconciliation",
  "VaultStatement",
  "DailyVolume"
];
var LEDGER_MAX_PAGE_SIZE = 100;
var LEDGER_DEFAULT_PAGE_SIZE = 50;
var LEDGER_DEFAULT_RECONCILIATION_LIMIT = 30;
function queryParams(entries) {
  const params = {};
  for (const [key, value] of Object.entries(entries)) {
    if (value !== void 0 && value !== null && value !== "") params[key] = value;
  }
  return params;
}
var ledgerEndpoints = (build) => ({
  listEntries: build.query({
    query: ({ pageSize, cursor, accountType, sourceType, status, search, fromDate, toDate }) => ({
      url: "/ledger/entries",
      params: queryParams({ pageSize, cursor, accountType, sourceType, status, search, fromDate, toDate })
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("LedgerEntry", result, "entryId"),
    keepUnusedDataFor: 120
  }),
  /** The balanced transaction behind an entry — legs, INR value, locked rate, payout ref. */
  getTransaction: build.query({
    query: (transactionId) => ({ url: `/ledger/transactions/${transactionId}` }),
    providesTags: (_r, _e, id) => providesOne("LedgerEntry", id)
  }),
  /**
   * Returns the **reversing** transaction (a fresh id with contra postings), not
   * the reversed one — so a caller that wants to show what happened reads the
   * response rather than re-fetching the original.
   *
   * Keyed on the reversed transaction id: a transaction reverses exactly once,
   * and `TransactionStatus.REVERSED` is terminal.
   */
  reverseTransaction: build.mutation({
    query: ({ transactionId, reason }) => ({
      url: `/ledger/transactions/${transactionId}/reverse`,
      method: "POST",
      body: { reason },
      idempotencyKey: `ledger-reverse:${transactionId}`
    }),
    invalidatesTags: (_r, _e, { transactionId }) => [
      ...providesOne("LedgerEntry", transactionId),
      { type: "LedgerEntry", id: "LIST" },
      { type: "LedgerAccount", id: "LIST" }
    ]
  }),
  /**
   * Accumulates by `nextCursor` like the paged endpoints, but hand-rolled because
   * the response is a bespoke envelope rather than `PagedApiResponse`.
   */
  listAccounts: build.query({
    query: ({ pageSize, cursor }) => ({
      url: "/ledger/accounts",
      params: queryParams({ pageSize, cursor })
    }),
    serializeQueryArgs: ({ queryArgs, endpointName }) => {
      const { cursor: _cursor, ...rest } = queryArgs ?? {};
      return `${endpointName}(${JSON.stringify(rest)})`;
    },
    merge: (currentCache, incoming, { arg }) => {
      if (!arg?.cursor) {
        Object.assign(currentCache, incoming);
        return;
      }
      currentCache.accounts.push(...incoming.accounts);
      currentCache.nextCursor = incoming.nextCursor ?? null;
      currentCache.totalAccounts = incoming.totalAccounts;
      currentCache.totalGoldHeldGrams = incoming.totalGoldHeldGrams;
    },
    forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
    providesTags: (result) => providesList("LedgerAccount", result?.accounts, "accountId"),
    keepUnusedDataFor: 60
  }),
  /**
   * Addressed by **customerId**, not accountId — and it is the self-service call
   * too. `@RequiresPermissionWithOwnership` serves the customer's own view and the
   * ops view from this one path, which is why there is no `/ledger/accounts/me`.
   */
  getAccount: build.query({
    query: (customerId) => ({ url: `/ledger/accounts/${customerId}` }),
    providesTags: (_r, _e, id) => providesOne("LedgerAccount", id),
    keepUnusedDataFor: 60
  }),
  /**
   * Paged entries for one customer — the statement *is* a page of legs. No date
   * range: the controller binds only `cursor` and `pageSize`. The three balance
   * totals a statement header wants come from `getAccount`, which is why the
   * Accounts screen makes both calls.
   */
  getAccountStatement: build.query({
    query: ({ customerId, pageSize, cursor }) => ({
      url: `/ledger/accounts/${customerId}/statement`,
      params: queryParams({ pageSize, cursor })
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (_r, _e, { customerId }) => [
      { type: "Statement", id: customerId }
    ]
  }),
  /** Its own envelope with `nextCursor` in the body, like `listAccounts` — not a `PagedApiResponse`. */
  getSchemeStatement: build.query({
    query: ({ customerId, schemeEnrollmentId, pageSize, cursor }) => ({
      url: `/ledger/accounts/${customerId}/schemes/${schemeEnrollmentId}/statement`,
      params: queryParams({ pageSize, cursor })
    }),
    providesTags: (_r, _e, { schemeEnrollmentId }) => [
      { type: "Statement", id: schemeEnrollmentId }
    ]
  }),
  freezeAccount: build.mutation({
    query: ({ accountId, reason, requestId }) => ({
      url: `/ledger/accounts/${accountId}/freeze`,
      method: "POST",
      body: { reason },
      idempotencyKey: `account-freeze:${accountId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { accountId }) => invalidatesOne("LedgerAccount", accountId)
  }),
  /** No body — the controller declares no `@RequestBody`, so a reason would be discarded. */
  unfreezeAccount: build.mutation({
    query: ({ accountId, requestId }) => ({
      url: `/ledger/accounts/${accountId}/unfreeze`,
      method: "POST",
      idempotencyKey: `account-unfreeze:${accountId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { accountId }) => invalidatesOne("LedgerAccount", accountId)
  }),
  /** Both bounds are required — the controller's `@RequestParam LocalDate` pair has no default. */
  getDailyVolume: build.query({
    query: ({ from, to }) => ({ url: "/ledger/reports/daily-volume", params: { from, to } }),
    providesTags: [{ type: "DailyVolume", id: "LIST" }],
    keepUnusedDataFor: 300
  }),
  /**
   * Omitting `status` yields `[OPEN, ESCALATED]` — open exceptions only, not
   * everything. The array is sent comma-delimited, which is what Spring's
   * `List<SuspenseCaseStatusDto>` binding accepts.
   */
  listSuspenseCases: build.query({
    query: ({ pageSize, cursor, status }) => ({
      url: "/ledger/suspense-cases",
      params: queryParams({
        pageSize,
        cursor,
        status: status && status.length > 0 ? status.join(",") : void 0
      })
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("SuspenseCase", result, "suspenseCaseId")
  }),
  /**
   * A resolution posts contra or reclassifying entries and moves the case to
   * `RESOLVED`, which is terminal (INV-12) — so the case id alone is a safe key.
   */
  resolveSuspenseCase: build.mutation({
    query: ({ id, resolutionType, correctCustomerId, note }) => ({
      url: `/ledger/suspense-cases/${id}/resolve`,
      method: "POST",
      body: {
        resolutionType,
        ...correctCustomerId ? { correctCustomerId } : {},
        ...note ? { note } : {}
      },
      idempotencyKey: `suspense-resolve:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("SuspenseCase", id),
      { type: "LedgerEntry", id: "LIST" },
      { type: "LedgerAccount", id: "LIST" }
    ]
  }),
  /** No body — escalation records no note on this service. */
  escalateSuspenseCase: build.mutation({
    query: ({ id }) => ({
      url: `/ledger/suspense-cases/${id}/escalate`,
      method: "POST",
      idempotencyKey: `suspense-escalate:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("SuspenseCase", id)
  }),
  // ─── Vault (self-custody: our own vault at a partner bank, third-party audited) ──
  /** A bounded recent-first log, not a page: `limit` only, no cursor and no date range. */
  listReconciliations: build.query({
    query: (args) => ({
      url: "/ledger/vault/reconciliations",
      params: queryParams({ limit: args?.limit })
    }),
    providesTags: (result) => providesList("VaultReconciliation", result, "reconciliationId")
  }),
  /** Detail carries the contributing entries and the statement lines behind a variance. */
  getReconciliation: build.query({
    query: (id) => ({ url: `/ledger/vault/reconciliations/${id}` }),
    providesTags: (_r, _e, id) => providesOne("VaultReconciliation", id)
  }),
  /**
   * `date` is a query parameter and there is no body. Idempotent twice over: the
   * handler no-ops a date already reconciled for this tenant (INV-13) and returns
   * the existing row unchanged, so a date-derived key is exactly right.
   *
   * The response is the completed row, so a caller does not need to poll — but the
   * day must already be closed in the tenant reporting zone or the command rejects it.
   */
  runReconciliation: build.mutation({
    query: ({ date }) => ({
      url: "/ledger/vault/reconciliations/run",
      method: "POST",
      params: { date },
      idempotencyKey: `vault-recon:${date}`
    }),
    invalidatesTags: [
      { type: "VaultReconciliation", id: "LIST" },
      { type: "VaultStatement", id: "LIST" }
    ]
  }),
  /** Body field is `note`; `@NotBlank`, so an empty string is a 400 rather than a clear. */
  annotateReconciliation: build.mutation({
    query: ({ reconciliationId, note, requestId }) => ({
      url: `/ledger/vault/reconciliations/${reconciliationId}/annotate`,
      method: "POST",
      body: { note },
      idempotencyKey: `vault-recon-annotate:${reconciliationId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { reconciliationId }) => invalidatesOne("VaultReconciliation", reconciliationId)
  }),
  /** One optional exact `date`, not a range. Omit it for the most recent across all dates. */
  listVaultStatements: build.query({
    query: (args) => ({
      url: "/ledger/vault/statements",
      params: queryParams({ date: args?.date })
    }),
    providesTags: (result) => providesList("VaultStatement", result, "statementId")
  }),
  uploadVaultStatement: build.mutation({
    query: ({ draftId, statementDate, source, statementRef, goldQuantityGrams }) => ({
      url: "/ledger/vault/statements",
      method: "POST",
      body: { statementDate, source, statementRef, goldQuantityGrams },
      idempotencyKey: `vault-statement:${statementDate}:${draftId}`
    }),
    invalidatesTags: [{ type: "VaultStatement", id: "LIST" }]
  }),
  /**
   * The body names the **date** of the reconciliation to match against, not its
   * id — `MatchVaultStatementCommand` resolves the day itself. A statement matches
   * once, so the statement id alone keys it.
   */
  matchVaultStatement: build.mutation({
    query: ({ statementId, reconciliationDate }) => ({
      url: `/ledger/vault/statements/${statementId}/match`,
      method: "POST",
      body: { reconciliationDate },
      idempotencyKey: `vault-statement-match:${statementId}`
    }),
    invalidatesTags: (_r, _e, { statementId }) => [
      ...invalidatesOne("VaultStatement", statementId),
      { type: "VaultReconciliation", id: "LIST" }
    ]
  })
});

// src/index.ts
var ledgerApi = createContextApi({
  context: "ledger",
  reducerPath: "ledgerApi",
  tagTypes: LEDGER_TAGS,
  mocks: () => import('./mocks-5HD2O7HS.js').then((module) => module.ledgerMocks),
  endpoints: ledgerEndpoints
});
var LEDGER_INVALIDATION_RULES = [
  {
    event: "payment.captured",
    invalidate: ({ customerId }) => [
      { type: "LedgerEntry", id: "LIST" },
      ...customerId ? [{ type: "LedgerAccount", id: customerId }] : [{ type: "LedgerAccount", id: "LIST" }]
    ]
  },
  {
    event: "redemption.settled",
    invalidate: () => [
      { type: "LedgerEntry", id: "LIST" },
      { type: "LedgerAccount", id: "LIST" }
    ]
  }
];
function registerLedgerInvalidation(store) {
  return registerCrossContextInvalidation({
    store,
    api: ledgerApi,
    rules: LEDGER_INVALIDATION_RULES
  });
}
var {
  useListEntriesQuery,
  useGetTransactionQuery,
  useReverseTransactionMutation,
  useListAccountsQuery,
  useGetAccountQuery,
  useGetAccountStatementQuery,
  useGetSchemeStatementQuery,
  useFreezeAccountMutation,
  useUnfreezeAccountMutation,
  useGetDailyVolumeQuery,
  useListSuspenseCasesQuery,
  useResolveSuspenseCaseMutation,
  useEscalateSuspenseCaseMutation,
  useListReconciliationsQuery,
  useGetReconciliationQuery,
  useRunReconciliationMutation,
  useAnnotateReconciliationMutation,
  useListVaultStatementsQuery,
  useUploadVaultStatementMutation,
  useMatchVaultStatementMutation
} = ledgerApi;

export { LEDGER_DEFAULT_PAGE_SIZE, LEDGER_DEFAULT_RECONCILIATION_LIMIT, LEDGER_INVALIDATION_RULES, LEDGER_MAX_PAGE_SIZE, LEDGER_TAGS, ledgerApi, registerLedgerInvalidation, useAnnotateReconciliationMutation, useEscalateSuspenseCaseMutation, useFreezeAccountMutation, useGetAccountQuery, useGetAccountStatementQuery, useGetDailyVolumeQuery, useGetReconciliationQuery, useGetSchemeStatementQuery, useGetTransactionQuery, useListAccountsQuery, useListEntriesQuery, useListReconciliationsQuery, useListSuspenseCasesQuery, useListVaultStatementsQuery, useMatchVaultStatementMutation, useResolveSuspenseCaseMutation, useReverseTransactionMutation, useRunReconciliationMutation, useUnfreezeAccountMutation, useUploadVaultStatementMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map